import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { Plus, Key, Trash2, Copy, Eye, EyeOff, Folder } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.frolic.so';

interface Project {
  id: string;
  name: string;
  apiKey: string;
  createdAt: string;
}

function ProjectsPage() {
  const { accessToken } = useAuth();
  const [newProjectName, setNewProjectName] = useState('');
  const [showApiKey, setShowApiKey] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { data: projectsData, refetch: refetchProjects } = useQuery<{ projects: Project[] }>({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/v1/user/projects`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch projects');
      return response.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      const response = await fetch(`${API_URL}/v1/user/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ name }),
      });
      if (!response.ok) throw new Error('Failed to create project');
      return response.json();
    },
    onSuccess: () => {
      refetchProjects();
      setNewProjectName('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (projectId: string) => {
      const response = await fetch(`${API_URL}/v1/user/projects/${projectId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to delete project');
      return response.json();
    },
    onSuccess: () => refetchProjects(),
  });

  const projects = projectsData?.projects || [];

  const copyApiKey = (apiKey: string, projectId: string) => {
    navigator.clipboard.writeText(apiKey);
    setCopiedId(projectId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Projects</h1>
        <p className="text-muted-foreground">Manage your logging projects and API keys</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Create New Project</CardTitle>
          <CardDescription>Add a new project to start collecting logs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              type="text"
              placeholder="Project name"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              className="flex-1"
            />
            <Button
              onClick={() => newProjectName && createMutation.mutate(newProjectName)}
              disabled={!newProjectName || createMutation.isPending}
            >
              <Plus className="mr-2 h-4 w-4" />
              {createMutation.isPending ? 'Creating...' : 'Create Project'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {projects.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <Folder className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-foreground">No projects yet</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Create your first project to start collecting logs
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {projects.map((project) => (
            <Card key={project.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-foreground">{project.name}</h3>
                      <Badge variant="secondary">Active</Badge>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>ID:</span>
                      <code className="rounded bg-muted px-2 py-0.5 font-mono text-xs">
                        {project.id}
                      </code>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowApiKey(showApiKey === project.id ? null : project.id)}
                    >
                      {showApiKey === project.id ? (
                        <>
                          <EyeOff className="mr-2 h-4 w-4" />
                          Hide Key
                        </>
                      ) : (
                        <>
                          <Eye className="mr-2 h-4 w-4" />
                          Show Key
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => {
                        if (confirm('Delete this project? This cannot be undone.')) {
                          deleteMutation.mutate(project.id);
                        }
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>

                {showApiKey === project.id && (
                  <div className="mt-4 rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-yellow-400">
                      <Key className="h-4 w-4" />
                      API Key (keep this secret!)
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <code className="flex-1 break-all rounded bg-background p-2 font-mono text-sm">
                        {project.apiKey}
                      </code>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => copyApiKey(project.apiKey, project.id)}
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        {copiedId === project.id ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/projects')({
  component: ProjectsPage,
});
